import {CommonModule} from '@angular/common';
import {IBaseComponent} from '../ibase/ibase.component';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {AfterViewInit, Component, EventEmitter, Input, Output} from '@angular/core';
import {JSONSchema7} from 'json-schema';
import {JsonSchemaFormPropertyComponent} from './json-schema-form-property.component';
import {
    DEFAULT_TAB_NAME,
    JsonSchemaFormContext,
    JsonSchemaFormParseResultColumn,
    JsonSchemaFormProperty,
    JsonSchemaFormUtils
} from './json-schema-form-utils';

/**
 * JsonSchemaForm component, built a form from a JSONSchema7 by using {@link JsonSchemaFormPropertyComponent}
 */
@Component({
    selector: 'app-json-schema-form',
    imports: [CommonModule, PrimeNgImportsModule, JsonSchemaFormPropertyComponent],
    templateUrl: './json-schema-form.component.html'
})
export class JsonSchemaFormComponent extends IBaseComponent implements AfterViewInit {
    protected _context!: JsonSchemaFormContext;
    protected _data!: unknown;
    protected _isWrapped = false;
    protected _props: JsonSchemaFormProperty[] = [];
    protected _columns: Record<string, JsonSchemaFormParseResultColumn[]> = {};
    protected _tabs: string[] = [DEFAULT_TAB_NAME];
    protected _activeTab = DEFAULT_TAB_NAME;
    protected _validationErrors: Record<string, string> = {};
    protected _isValid = false;
    private _validityUpdateTimer?: ReturnType<typeof setTimeout>;

    // ===============================================================
    // ===============================================================

    @Input()
    public schema?: JSONSchema7;
    @Input()
    public rootSchema?: JSONSchema7;
    @Input()
    public initData?: unknown;
    @Input()
    public readOnly = false;

    public get isValid() {
        return this._isValid;
    }

    public get context() {
        return this._context;
    }

    @Output()
    public validityChangedEvent = new EventEmitter<boolean>();

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        if (!this.schema) return;

        this._context = new JsonSchemaFormContext(this.rootSchema ?? this.schema);
        this.schema = JsonSchemaFormUtils.resolveSchema(this._context, this.schema);

        if (this.schema.properties) {
            this.handleSchemaProperties();
        } else if (this.schema.type) {
            this.handleSchemaSingleProperty();
        }

        if (this.initData !== undefined && this.initData !== null) {
            this.setData(this.initData);
        } else {
            this._data = this._isWrapped ? undefined : {};
            this._data = this._isWrapped ? {Value: this._data} : {};
        }

        this._context.rootData = this._data;
        this.prepareFormData();
        JsonSchemaFormUtils.refreshContextRowReferencesOptions(this._context);

        this._activeTab = this._tabs[0];
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        if (this._validityUpdateTimer) {
            clearTimeout(this._validityUpdateTimer);
        }
    }
    /**
     * When the view is ready
     */
    public ngAfterViewInit(): void {
        this.onValidityChanged();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Handle schema multi properties
     */
    private handleSchemaProperties() {
        if (!this.schema?.properties) return;

        const parsed = JsonSchemaFormUtils.parseSchema(this._context, this.schema);
        this._props = parsed.properties;
        this._columns = parsed.columns;
        this._tabs = parsed.tabs;
    }
    /**
     * Handle schema single property (AKA wrapped)
     */
    private handleSchemaSingleProperty() {
        if (!this.schema) return;

        const parsed = JsonSchemaFormUtils.parseSchema(this._context, {
            properties: {
                Value: this.schema
            }
        });
        this._props = parsed.properties;
        this._columns = parsed.columns;
        this._tabs = parsed.tabs;

        this._isWrapped = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the validity changed
     */
    protected onValidityChanged() {
        if (this._validityUpdateTimer) {
            clearTimeout(this._validityUpdateTimer);
        }

        this._validityUpdateTimer = setTimeout(() => {
            this._validityUpdateTimer = undefined;
            this._isValid = this.validateFormData();
            this.validityChangedEvent.next(this._isValid);
        }, 50);
    }

    /**
     * Prepare defaults and date instances for the complete data tree, including rows not currently rendered.
     */
    private prepareFormData(): void {
        const data = this._data as Record<string | number, unknown>;
        this._props.forEach((prop) => JsonSchemaFormUtils.prepareValueForSchema(prop, data, prop.id));
    }

    /**
     * Validate the complete data tree independently from how many controls are currently rendered.
     */
    private validateFormData(): boolean {
        Object.keys(this._validationErrors).forEach((key) => delete this._validationErrors[key]);

        const data = this._data as Record<string | number, unknown>;
        this._props.forEach((prop) =>
            JsonSchemaFormUtils.validateValueForSchema(prop, data[prop.id], prop.id, this._validationErrors)
        );

        return Object.keys(this._validationErrors).length == 0;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Set form data
     * @param data Raw data
     */
    public setData(data: unknown) {
        this._data = this._isWrapped ? {Value: data} : data;
        if (this._context) {
            this._context.rootData = this._data;
            JsonSchemaFormUtils.refreshContextRowReferencesOptions(this._context);
        }
    }
    /**
     * Get form data
     * @returns Raw data
     */
    public getData(): unknown {
        const raw = this._isWrapped
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (this._data as any)['Value']
            : this._data;
        return raw;
    }
}
